import gsap from "gsap";
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import Experience from "../Experience";
import type Resources from "../utils/Resources";

interface FadeMaterial extends THREE.Material {
  opacity: number;
  transparent: boolean;
}

export default class PaperBackgroundV2 {
  public mesh: THREE.Object3D;
  public group = new THREE.Group();

  private experience: Experience;
  private materials: FadeMaterial[] = [];
  private paperTexture?: THREE.Texture;
  private resources: Resources;
  private mixer?: THREE.AnimationMixer;
  private animations: THREE.AnimationClip[] = [];

  constructor() {
    this.experience = new Experience();
    this.resources = this.experience.resources;
    this.mesh = new THREE.Group();
    this.mesh.visible = false;
    this.loadModel();
  }

  private loadModel() {
    const gltf = this.resources.get<GLTF>("paperBackgroundModel");
    if (!gltf) {
      console.warn("CoolS3D: GLB model 'coolSModel' not found in resources");
      return;
    }

    // Load moon textures
    const texture = this.resources.get<THREE.Texture>("paperTexture")!;
    this.paperTexture = texture; // Store reference for later use

    // Configure textures for proper rendering
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.needsUpdate = true;
    texture.repeat.set(1, 1);

    const sceneClone = gltf.scene.clone(true);

    // Store animations
    if (gltf.animations && gltf.animations.length > 0) {
      this.animations = gltf.animations;
      this.mixer = new THREE.AnimationMixer(sceneClone);
    }

    sceneClone.castShadow = false;
    sceneClone.receiveShadow = false;
    sceneClone.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        mesh.castShadow = false;
        mesh.receiveShadow = false;

        // Apply moon textures to the material
        // Dispose old material to prevent memory leaks
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((mat) => mat.dispose());
        } else {
          mesh.material.dispose();
        }

        const material = new THREE.MeshStandardMaterial({
          map: texture,
          emissive: new THREE.Color(0xffffff),
          emissiveMap: texture,
          emissiveIntensity: 0.6, // Adjust between 0-1
          // roughness: 1.0,
          // metalness: 0.0,
          side: THREE.DoubleSide,
          // opacity: 1,
          // transparent: true,
          // alphaTest: 0.1,
          transparent: false,
        });
        // material.depthFunc = THREE.EqualDepth;

        material.dithering = false;
        material.needsUpdate = true;
        material.depthWrite = false;

        // Ensure the geometry has UV2 for AO map
        if (!mesh.geometry.attributes.uv2) {
          mesh.geometry.setAttribute(
            "uv2",
            mesh.geometry.attributes.uv.clone()
          );
        }

        mesh.material = material;
      }
    });

    // Orient the model so it stands upright facing the camera
    sceneClone.rotation.set(-Math.PI / 2, 0, 0);

    this.group.add(sceneClone);
    this.group.position.z = -100;
    this.fitToViewport();
    this.experience.scene.add(this.group);
  }

  show() {
    this.mesh.visible = true;
    this.setOpacity(1);
  }

  hide() {
    this.mesh.visible = false;
  }

  setVisibility(visible: boolean) {
    this.group.visible = visible;
  }

  fitToViewport(sizeMultiplier: number = 0.2) {
    const camera = this.experience.camera.instance;
    const distance = Math.abs(camera.position.z - this.group.position.z);

    // Calculate visible height at the distance
    const vFov = (camera.fov * Math.PI) / 180; // Convert vertical fov to radians
    const visibleHeight = 2 * Math.tan(vFov / 2) * distance;
    const visibleWidth = visibleHeight * camera.aspect;

    const maxSize = Math.max(visibleWidth, visibleHeight) * sizeMultiplier;

    // Set scale to match viewport
    this.group.scale.set(maxSize, maxSize, 1);

    if (this.paperTexture) {
      const aspectRatio = visibleWidth / visibleHeight;
      const repeatPattern = Math.max(1, aspectRatio * 0.8);
      this.paperTexture.repeat.set(repeatPattern, repeatPattern);
      this.paperTexture.needsUpdate = true;
    }
  }

  setOpacity(value: number) {
    const clamped = THREE.MathUtils.clamp(value, 0, 1);

    for (const mat of this.materials) {
      mat.opacity = clamped;
      mat.transparent = clamped < 1;
      if (mat.depthWrite !== undefined) {
        mat.depthWrite = clamped >= 1;
      }
      mat.needsUpdate = true;
    }

    this.mesh.visible = clamped > 0.001;
  }

  playAnimation(speed: number = 1, onFinish?: () => void) {
    const index = 0;
    if (!this.mixer || !this.animations.length) {
      console.warn("PaperBackgroundV2: No animations available");
      return;
    }

    const clip = this.animations[index];
    if (!clip) {
      console.warn(`PaperBackgroundV2: Animation index ${index} not found`);
      return;
    }

    this.mixer.timeScale = speed;
    const action = this.mixer.clipAction(clip);
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true; // Keep the last frame when animation ends
    action.reset(); // Reset to ensure clean start

    if (onFinish) {
      const onFinishedHandler = () => {
        onFinish();
        this.mixer?.removeEventListener("finished", onFinishedHandler);
      };
      this.mixer.addEventListener("finished", onFinishedHandler);
    }

    action.play();
  }

  playCrumpleAnimation(duration: number, onFinish?: () => void) {
    const scale = 2.5;
    gsap.to(this.group.scale, {
      x: this.group.scale.x * scale,
      y: this.group.scale.y * scale,
      z: this.group.scale.z * scale,
      duration,
      ease: "linear",
    });

    this.playAnimation(0.1, () => {
      // After crumple finishes, start implosion
      gsap.to(this.group.scale, {
        x: 0,
        y: 0,
        z: 0,
        duration: 1,
        ease: "power4.in",
        onComplete: () => {
          if (onFinish) onFinish();
        },
      });
    });
  }

  update(deltaTime: number) {
    if (this.mixer) {
      this.mixer.update(deltaTime);
    }
  }

  dispose() {
    // Stop all animations
    if (this.mixer) {
      this.mixer.stopAllAction();
    }

    // Dispose materials
    this.materials.forEach((material) => {
      material.dispose();
    });

    // Dispose texture
    if (this.paperTexture) {
      this.paperTexture.dispose();
    }

    // Dispose geometries and materials in the mesh
    this.group.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) {
          mesh.geometry.dispose();
        }
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((mat) => mat.dispose());
        } else if (mesh.material) {
          mesh.material.dispose();
        }
      }
    });

    // Remove from scene
    this.experience.scene.remove(this.group);

    // Clear arrays
    this.materials = [];
  }
}
